import { useState, type CSSProperties, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OVERTIME_STATUS_LABELS, kstHm, kstToday, overtimeMinutes, planOvertime, type OvertimeRecord, type OvertimeStatus } from '@dxtech/shared';
import { AddRegular, PlayRegular, StopRegular } from '@fluentui/react-icons';
import { overtimeApi } from '../api/overtime.js';
import { useApp } from '../state/app.js';
import { ConfirmDialog, ModalFrame, Spinner } from '../ui/common.js';

export const OT_STYLE: Record<OvertimeStatus, CSSProperties> = {
  planned: { background: '#EEF2F6', color: '#334155' },
  in_progress: { background: '#FEF3C7', color: '#B45309' },
  done: { background: '#DCFCE7', color: '#15803D' },
  canceled: { background: '#F3F4F6', color: '#9CA3AF' },
};
export const OvertimeBadge = ({ status }: { status: OvertimeStatus }) => (
  <span style={{ ...OT_STYLE[status], fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>{OVERTIME_STATUS_LABELS[status]}</span>
);
const md = (date: string) => { const d = new Date(`${date}T00:00:00Z`); return `${d.getUTCMonth() + 1}/${d.getUTCDate()}(${'일월화수목금토'[d.getUTCDay()]})`; };
export const dur = (min: number) => (min >= 60 ? `${Math.floor(min / 60)}시간 ${min % 60 ? `${min % 60}분` : ''}`.trim() : `${min}분`);
/** 예정 '18:30~22:00', 다음 날 종료면 '18:30~익일 02:00' */
export const planSpan = (r: OvertimeRecord) => `${kstHm(r.planStart)}~${kstToday(new Date(r.planEnd)) !== r.date ? '익일 ' : ''}${kstHm(r.planEnd)}`;
export const actualSpan = (r: OvertimeRecord) => (r.actualStart ? `${kstHm(r.actualStart)}~${r.actualEnd ? kstHm(r.actualEnd) : ''}` : '');

// 야근 — 당일 신청 → 시작·종료 버튼으로 실제 시각 기록
export function Overtime(): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['overtime', 'mine'], queryFn: () => overtimeApi.mine(), refetchInterval: 60_000 });
  const items = q.data?.items ?? [];
  const today = kstToday();
  // 지금 처리할 야근 — 진행 중 → 오늘(또는 자정 넘긴 어제) 예정 순
  const current = items.find((r) => r.status === 'in_progress')
    ?? items.find((r) => r.status === 'planned' && r.date === today);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<OvertimeRecord | null>(null);

  const act = async (r: OvertimeRecord, a: 'start' | 'end' | 'cancel') => {
    setBusy(true);
    try {
      await overtimeApi.act(r.id, a);
      flash({ start: '야근을 시작했습니다', end: '야근을 종료했습니다', cancel: '야근 신청을 취소했습니다' }[a]);
      await qc.invalidateQueries({ queryKey: ['overtime'] });
    } catch (e) { flash(e instanceof Error ? e.message : '처리 실패'); } finally { setBusy(false); }
  };

  const cell: CSSProperties = { padding: '11px 14px', fontSize: 13, borderTop: '1px solid #F3F4F6' };
  return (
    <div style={{ maxWidth: 880, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#6B7280' }}>오늘 야근을 신청하고, 시작·종료 버튼으로 실제 시간을 남깁니다.</div>
        <button className="dx-btn dx-btn-primary" style={{ marginLeft: 'auto', fontSize: 13 }} onClick={() => setOpen(true)}><AddRegular aria-hidden style={{ fontSize: 16 }} />야근 신청</button>
      </div>

      {/* 오늘의 야근 — 시작·종료 */}
      <div className="dx-card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {q.isLoading ? <Spinner size={20} /> : current ? (
          <>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{current.date === today ? '오늘' : md(current.date)} 야근</span>
                <OvertimeBadge status={current.status} />
              </div>
              <div style={{ fontSize: 13, color: '#4B5563', marginTop: 4 }}>
                예정 {planSpan(current)}
                {current.actualStart && <span style={{ marginLeft: 12 }}>시작 {kstHm(current.actualStart)} ({dur(overtimeMinutes(current))} 경과)</span>}
              </div>
              {current.reason && <div style={{ fontSize: 12.5, color: '#9CA3AF', marginTop: 2 }}>{current.reason}</div>}
            </div>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
              {current.status === 'planned' && (
                <button className="dx-btn dx-btn-primary" disabled={busy} onClick={() => void act(current, 'start')}><PlayRegular aria-hidden style={{ fontSize: 16 }} />야근 시작</button>
              )}
              {current.status === 'in_progress' && (
                <button className="dx-btn dx-btn-danger" disabled={busy} onClick={() => void act(current, 'end')}><StopRegular aria-hidden style={{ fontSize: 16 }} />야근 종료</button>
              )}
            </span>
          </>
        ) : (
          <div style={{ fontSize: 13.5, color: '#6B7280' }}>오늘 신청한 야근이 없습니다. 야근이 필요하면 먼저 ‘야근 신청’을 해 주세요.</div>
        )}
      </div>

      {/* 내 야근 내역 — 이번 달·다음 달 */}
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', fontSize: 14, fontWeight: 700, borderBottom: '1px solid #F3F4F6' }}>내 야근 내역</div>
        {q.isLoading ? <div style={{ padding: 28, textAlign: 'center' }}><Spinner size={20} /></div> : items.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>야근 신청 내역이 없습니다.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead><tr style={{ background: '#F9FAFB', textAlign: 'left', fontSize: 12, color: '#6B7280' }}>
                {['날짜', '예정', '실제', '시간', '상태', ''].map((h) => <th key={h} style={{ padding: '9px 14px', fontWeight: 600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} style={{ opacity: r.status === 'canceled' ? 0.55 : 1 }}>
                    <td style={{ ...cell, whiteSpace: 'nowrap', fontWeight: 600 }}>{md(r.date)}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }} className="tnum">{planSpan(r)}{r.reason && <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{r.reason}</div>}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }} className="tnum">{actualSpan(r) || '-'}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{r.actualStart ? dur(overtimeMinutes(r)) : '-'}</td>
                    <td style={cell}><OvertimeBadge status={r.status} /></td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      {r.status === 'planned' && <button className="dx-btn dx-btn-ghost dx-btn-sm" disabled={busy} onClick={() => setCancelTarget(r)}>취소</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && <OvertimeModal onClose={() => setOpen(false)} />}
      {cancelTarget && (
        <ConfirmDialog title="야근 신청 취소" message={`${md(cancelTarget.date)} ${planSpan(cancelTarget)} 야근 신청을 취소할까요?`} confirmLabel="신청 취소" danger
          onConfirm={() => { const r = cancelTarget; setCancelTarget(null); void act(r, 'cancel'); }} onCancel={() => setCancelTarget(null)} />
      )}
    </div>
  );
}

/** 야근 신청 — 오늘 건만, 시작(18:00 이후), 종료(같은 날 또는 다음 날 09:00 전) */
export function OvertimeModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const date = kstToday(); // 자리비움처럼 당일만
  const [start, setStart] = useState('18:00');
  const [end, setEnd] = useState('21:00');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const check = planOvertime(date, start, end);
  const error = 'error' in check ? check.error : null;
  const nextDay = !error && end <= start;
  const submit = async () => {
    setBusy(true);
    try {
      await overtimeApi.create(date, start, end, reason);
      flash('야근을 신청했습니다');
      await qc.invalidateQueries({ queryKey: ['overtime'] });
      onClose();
    } catch (e) { flash(e instanceof Error ? e.message : '신청 실패'); } finally { setBusy(false); }
  };
  const input: CSSProperties = { width: '100%', boxSizing: 'border-box', height: 38 };
  return (
    <ModalFrame title="야근 신청" sub="시작은 오후 6시 이후, 종료는 다음 날 오전 9시 전까지 정할 수 있습니다." width={460} onClose={onClose}
      footer={<>
        <span style={{ flex: 1 }} />
        <button className="dx-btn dx-btn-ghost" onClick={onClose}>취소</button>
        <button className="dx-btn dx-btn-primary" disabled={busy || !!error} onClick={() => void submit()}>{busy ? '신청 중…' : '신청'}</button>
      </>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1', fontSize: 13, color: '#374151' }}>
          <span className="dx-field-label" style={{ display: 'inline', marginRight: 8 }}>야근 날짜</span>오늘 ({date})
        </div>
        <label><span className="dx-field-label">시작 예정</span>
          <input type="time" className="dx-input" style={input} min="18:00" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label><span className="dx-field-label">종료 예정{nextDay && <span style={{ color: '#2563EB', fontWeight: 600 }}> (다음 날)</span>}</span>
          <input type="time" className="dx-input" style={input} value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        <label style={{ gridColumn: '1 / -1' }}><span className="dx-field-label">업무 내용</span>
          <input className="dx-input" style={input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="예) 국도화학 착수 보고서 마무리" /></label>
      </div>
      {error && <div style={{ marginTop: 12, fontSize: 12.5, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 10px' }}>{error}</div>}
    </ModalFrame>
  );
}

/** 홈 '오늘의 근무' 카드 아래 한 줄 — 오늘 야근 신청·시작·종료(야근 화면과 같은 데이터·규칙) */
export function TodayOvertime(): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['overtime', 'mine'], queryFn: () => overtimeApi.mine(), refetchInterval: 60_000 });
  const today = kstToday();
  const items = q.data?.items ?? [];
  const r = items.find((x) => x.status === 'in_progress') ?? items.find((x) => x.date === today && x.status !== 'canceled');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const act = async (a: 'start' | 'end' | 'cancel') => {
    if (!r) return;
    setBusy(true);
    try {
      await overtimeApi.act(r.id, a);
      flash({ start: '야근을 시작했습니다', end: '야근을 종료했습니다', cancel: '야근 신청을 취소했습니다' }[a]);
      await qc.invalidateQueries({ queryKey: ['overtime'] });
    } catch (e) { flash(e instanceof Error ? e.message : '처리 실패'); } finally { setBusy(false); }
  };
  const btn: CSSProperties = { padding: '6px 12px', fontSize: 12.5 };
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #F1F3F5', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 34 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>오늘 야근</span>
      {q.isLoading ? <Spinner size={14} /> : !r ? (
        <>
          <span style={{ fontSize: 12.5, color: '#9CA3AF' }}>신청 없음</span>
          <button className="dx-btn dx-btn-ghost" style={{ ...btn, marginLeft: 'auto' }} onClick={() => setOpen(true)}><AddRegular aria-hidden style={{ fontSize: 14 }} />야근 신청</button>
        </>
      ) : (
        <>
          <OvertimeBadge status={r.status} />
          <span className="tnum" style={{ fontSize: 12.5, color: '#4B5563' }}>
            {r.status === 'planned' && `예정 ${planSpan(r)}`}
            {r.status === 'in_progress' && `${kstHm(r.actualStart)} 시작, ${dur(overtimeMinutes(r))} 경과`}
            {r.status === 'done' && `${actualSpan(r)} (${dur(overtimeMinutes(r))})`}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
            {r.status === 'planned' && <>
              <button className="dx-btn dx-btn-ghost" style={btn} disabled={busy} onClick={() => setConfirmCancel(true)}>취소</button>
              <button className="dx-btn dx-btn-primary" style={btn} disabled={busy} onClick={() => void act('start')}><PlayRegular aria-hidden style={{ fontSize: 14 }} />야근 시작</button>
            </>}
            {r.status === 'in_progress' && (
              <button className="dx-btn dx-btn-danger" style={btn} disabled={busy} onClick={() => void act('end')}><StopRegular aria-hidden style={{ fontSize: 14 }} />야근 종료</button>
            )}
          </span>
        </>
      )}
      {open && <OvertimeModal onClose={() => setOpen(false)} />}
      {confirmCancel && r && (
        <ConfirmDialog title="야근 신청 취소" message={`오늘 ${planSpan(r)} 야근 신청을 취소할까요?`} confirmLabel="신청 취소" danger
          onConfirm={() => { setConfirmCancel(false); void act('cancel'); }} onCancel={() => setConfirmCancel(false)} />
      )}
    </div>
  );
}
